import DemoBanner from "./DemoBanner";
import { Shield, Phone, Star, Wrench, Home, CloudRain, CheckCircle, ChevronRight, HardHat } from "lucide-react";
import { useState } from "react";

const stats = [
  { value: "500+", label: "Roofs Completed" },
  { value: "15", label: "Years in Business" },
  { value: "4.9", label: "Star Rating" },
  { value: "100%", label: "Satisfaction Guarantee" },
];

const services = [
  { icon: Home, title: "Roof Replacement", desc: "Full tear-off and new installation. Asphalt shingles, metal, tile, and flat roofing systems." },
  { icon: Wrench, title: "Roof Repair", desc: "Leak detection, storm damage repair, flashing, vent replacement, and emergency patching." },
  { icon: Shield, title: "Inspections & Maintenance", desc: "Annual inspections, preventative maintenance, and detailed condition reports for homeowners and realtors." },
  { icon: CloudRain, title: "Storm Damage", desc: "We work directly with your insurance company. Documentation, claims support, and fast restoration." },
  { icon: HardHat, title: "Commercial Roofing", desc: "TPO, EPDM, modified bitumen, and built-up roofing for commercial and industrial properties." },
  { icon: Star, title: "Gutters & Siding", desc: "Seamless gutter installation, gutter guards, and vinyl or fiber cement siding." },
];

const testimonials = [
  {
    quote: "Summit replaced our entire roof in two days. Professional crew, clean job site, and the price was exactly what they quoted. No surprises.",
    name: "Mike & Karen D.",
    location: "Residential — Oakdale",
  },
  {
    quote: "After the hailstorm, Summit handled everything with our insurance. We didn't have to lift a finger. New roof looks incredible.",
    name: "James T.",
    location: "Storm Damage — Westfield",
  },
  {
    quote: "We've used them on three commercial buildings now. Always on time, always on budget. They're our go-to.",
    name: "Sarah L.",
    location: "Commercial — Downtown",
  },
];

export default function SummitRoofing() {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", message: "" });

  return (
    <div data-testid="demo-summit-roofing" style={{ fontFamily: "'Work Sans', sans-serif" }} className="bg-[#14181E] text-white min-h-screen">

      {/* Top bar */}
      <div className="bg-[#E8621A] px-6 md:px-16 py-2.5 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-white/90">Licensed &middot; Bonded &middot; Insured</span>
        <a href="tel:5551234567" className="text-sm font-bold text-white flex items-center gap-1.5">
          <Phone size={14} /> (555) 123-4567
        </a>
      </div>

      {/* Nav */}
      <nav className="bg-[#14181E] px-6 md:px-16 py-4 flex items-center justify-between border-b border-white/8">
        <div>
          <span className="text-2xl md:text-3xl font-black uppercase tracking-tight">
            Summit<span className="text-[#E8621A]"> Roofing</span>
          </span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-wide text-white/50">
          <a href="#services" className="hover:text-white transition-colors">Services</a>
          <a href="#reviews" className="hover:text-white transition-colors">Reviews</a>
          <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          <button onClick={() => document.getElementById('contact')?.scrollIntoView({behavior:'smooth'})} className="bg-[#E8621A] text-white px-6 py-2.5 font-bold uppercase text-sm hover:bg-[#d45510] transition-colors">
            Free Estimate
          </button>
        </div>
      </nav>

      {/* Hero — full width image with dark overlay */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1639987650487-e10b3ae94fe6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxyb29maW5nJTIwY29udHJhY3RvciUyMHdvcmtlciUyMG9uJTIwcm9vZiUyMGFlcmlhbCUyMGRhcmt8ZW58MHx8fHwxNzc2MjkzNzExfDA&ixlib=rb-4.1.0&q=85"
            alt="Roofer at work"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#14181E]/75" />
        </div>

        <div className="relative px-6 md:px-16 py-28 md:py-40 lg:py-48 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black uppercase leading-[1.05] tracking-tight">
            The roof over
            <br />
            your head <span className="text-[#E8621A]">matters.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/50 mt-6 max-w-xl leading-relaxed font-medium">
            15 years protecting homes and businesses. Quality materials, expert crews, and a guarantee that means something.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({behavior:'smooth'})} className="bg-[#E8621A] text-white px-8 py-4 font-bold uppercase text-sm tracking-wide hover:bg-[#d45510] transition-colors flex items-center gap-2">
              Get a Free Estimate <ChevronRight size={18} />
            </button>
            <a href="tel:5551234567" className="border-2 border-white/20 text-white px-8 py-4 font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:border-white/50 transition-colors">
              <Phone size={18} /> (555) 123-4567
            </a>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <div className="bg-[#E8621A]">
        <div className="max-w-6xl mx-auto px-6 md:px-16 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-3xl md:text-4xl font-black text-white">{s.value}</p>
              <p className="text-xs text-white/70 font-bold uppercase tracking-wider mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Services */}
      <section id="services" className="py-24 md:py-32 px-6 md:px-16 bg-[#14181E]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <span className="text-[#E8621A] text-xs font-bold uppercase tracking-widest">What We Do</span>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2">
              Our Services
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.title} className="bg-white/[0.04] border border-white/8 p-7 hover:border-[#E8621A]/40 transition-colors group">
                <s.icon size={28} className="text-[#E8621A]" />
                <h3 className="text-lg font-bold uppercase mt-5">{s.title}</h3>
                <p className="text-sm text-white/40 mt-3 leading-relaxed font-medium">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo break */}
      <div className="relative h-72 md:h-96 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1770044001869-12315d4d6884?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxob3VzZSUyMHJvb2YlMjBzaGluZ2xlcyUyMGluc3RhbGxhdGlvbiUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3NzYyOTM3Njh8MA&ixlib=rb-4.1.0&q=85"
          alt="Roof shingles detail"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14181E] via-transparent to-[#14181E] opacity-60" />
      </div>

      {/* Why Us */}
      <section className="py-24 md:py-32 px-6 md:px-16 bg-[#14181E]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#E8621A] text-xs font-bold uppercase tracking-widest">Why Summit</span>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2 mb-8">
              Built Different.
            </h2>
            <div className="space-y-5">
              {[
                "Free inspections and honest assessments — no pressure, ever",
                "Premium materials with 25–50 year manufacturer warranties",
                "Our crews are employees, not subcontractors",
                "We handle your insurance claim paperwork start to finish",
                "Clean job site guaranteed — we treat your property like ours",
                "Written warranty on all labor, in addition to materials",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-[#E8621A] flex-shrink-0 mt-0.5" />
                  <p className="text-white/60 font-medium text-sm">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1714939825216-1663f2cc4b0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxzdWJ1cmJhbiUyMGhvdXNlJTIwYmVhdXRpZnVsJTIwcm9vZiUyMG92ZXJoZWFkJTIwZHJvbmV8ZW58MHx8fHwxNzc2MjkzNzg3fDA&ixlib=rb-4.1.0&q=85"
              alt="Aerial view of residential roof"
              className="w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="reviews" className="py-24 md:py-32 px-6 md:px-16 bg-[#0E1117] border-t border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14">
            <span className="text-[#E8621A] text-xs font-bold uppercase tracking-widest">What Our Clients Say</span>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2">
              Real Reviews
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white/[0.03] border border-white/8 p-7">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#E8621A" stroke="#E8621A" />)}
                </div>
                <p className="text-white/60 text-sm leading-relaxed font-medium">"{t.quote}"</p>
                <div className="mt-6 pt-4 border-t border-white/5">
                  <p className="text-white font-bold text-sm">{t.name}</p>
                  <p className="text-white/30 text-xs mt-0.5">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 md:py-32 px-6 md:px-16 bg-[#14181E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#E8621A] text-xs font-bold uppercase tracking-widest">Get Started</span>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2">
              Request Your Free Estimate
            </h2>
            <p className="text-white/40 mt-4 max-w-md mx-auto font-medium">
              Fill out the form below or call us directly. We respond within 24 hours.
            </p>
          </div>

          <form className="max-w-2xl mx-auto space-y-5" onSubmit={(e) => e.preventDefault()}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-white/40 mb-2 block">Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#E8621A] transition-colors"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-white/40 mb-2 block">Phone</label>
                <input
                  type="tel"
                  placeholder="(555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#E8621A] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-white/40 mb-2 block">Email</label>
              <input
                type="email"
                placeholder="you@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-white/40 mb-2 block">Tell us about your project</label>
              <textarea
                rows={4}
                placeholder="What type of roofing work do you need?"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/10 text-white text-sm resize-none placeholder-white/20 focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>
            <button className="w-full bg-[#E8621A] text-white py-4 font-bold uppercase text-sm tracking-wide hover:bg-[#d45510] transition-colors">
              Submit Request
            </button>
          </form>

          <div className="text-center mt-8">
            <p className="text-white/30 text-sm">Or call us directly:</p>
            <a href="tel:5551234567" className="text-[#E8621A] text-2xl font-black mt-2 block hover:text-white transition-colors">
              (555) 123-4567
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0E1117] border-t border-white/5 py-8 px-6 md:px-16 text-center">
        <span className="text-white/20 text-sm font-medium">{new Date().getFullYear()} Summit Roofing. All rights reserved.</span>
      </footer>

      <div className="pb-16" />
      <DemoBanner />
    </div>
  );
}
